import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import LegalEntity from "legal-entities/legal-entity";
import RegistrationInfoSchema, {
  RegistrationInfoJSON,
} from "legal-entities/registration-info/_schema/registration-info-schema";
import RegistrationInfo from "legal-entities/registration-info/registration-info";
import { z } from "zod";

export type AdministratorInvitationJSON = z.infer<typeof AdministratorInvitationSchema.AdministratorInvitationJSONZ>;
export type AdministratorInvitationParams = z.infer<typeof AdministratorInvitationSchema.AdministratorInvitationZ>;

export default class AdministratorInvitationSchema {
  public static readonly AdministratorInvitationJSONZ = z.object({
    id: z.uuid(),
    entityId: z.uuid(),
    entityName: z.string(),
    isClient: z.boolean().nullish(),
    isVendor: z.boolean().nullish(),
    email: z.email(),
    firstName: z.string(),
    lastName: z.string(),
    status: z.enum(InvitationStatus),
    message: z.string().nullish(),
  });

  public static readonly AdministratorInvitationZ = z.object({
    id: z.instanceof(Guid),
    entity: z.instanceof(LegalEntity),
    registrationInfo: z.instanceof(RegistrationInfo),
    status: z.enum(InvitationStatus),
    message: z.string().optional(),
  });

  public static validate(values: unknown): AdministratorInvitationParams {
    return parseOrThrow(AdministratorInvitationSchema.AdministratorInvitationZ, values, {
      logError: true,
      schemaName: "AdministratorInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): AdministratorInvitationJSON {
    return parseOrThrow(AdministratorInvitationSchema.AdministratorInvitationJSONZ, json, {
      logError: true,
      schemaName: "AdministratorInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is AdministratorInvitationParams {
    try {
      AdministratorInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AdministratorInvitationJSON {
    try {
      AdministratorInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): AdministratorInvitationParams {
    const parsed = parseOrThrow(AdministratorInvitationSchema.AdministratorInvitationJSONZ, json, {
      logError: true,
      schemaName: "AdministratorInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AdministratorInvitationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AdministratorInvitationParams): AdministratorInvitationJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AdministratorInvitationSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AdministratorInvitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AdministratorInvitationSchema.AdministratorInvitationJSONZ,
    AdministratorInvitationSchema.AdministratorInvitationZ,
    {
      decode: (json) => ({
        id: new Guid(GuidSchema.deserialize(json.id)),
        entity: LegalEntity.fromJSON({
          id: json.entityId,
          name: json.entityName,
          isClient: json.isClient ?? undefined,
          isVendor: json.isVendor ?? undefined,
        }),
        registrationInfo: new RegistrationInfo(
          RegistrationInfoSchema.deserialize({
            ...json,
            name: { firstName: json.firstName, lastName: json.lastName },
          } satisfies RegistrationInfoJSON),
        ),
        status: json.status,
        message: json.message ?? undefined,
      }),
      encode: (params) => ({
        id: params.id.toJSON(),
        entityId: params.entity.id.toJSON(),
        entityName: EntityNameSchema.validateJSON(params.entity.name?.toJSON()),
        isClient: params.entity.isClient,
        isVendor: params.entity.isVendor,
        email: params.registrationInfo.email.toJSON(),
        firstName: z.string().min(1).parse(params.registrationInfo.name.firstName),
        lastName: z.string().min(1).parse(params.registrationInfo.name.lastName),
        status: params.status,
        message: params.message,
      }),
    },
  );
}
